import { ThunkDispatch } from '@reduxjs/toolkit';
import { AppState } from '../stores/redux-store';
import { ResponseObj } from '../lib-api/services/base';
import { CredentialsEntity } from '../models/credentials';
import { refreshCredentialsEnd, refreshCredentialsStart } from '../stores/credentials-store';
import {
  refreshCredentials as refreshCredentialsImport,
  RefreshCredentialsRequest,
} from '../lib-api/services/Credentials';
import {
  RefreshCredentialsPayload,
  signCredentialsWithStatusUpdated as signCredentialsWithStatusUpdatedImport,
} from '../connectivity/services/credentials';

export interface CredentialsClient {
  refreshCredentials: (props: RefreshCredentialsRequest) => Promise<ResponseObj<null>>;
  signCredentialsWithStatusUpdated: typeof signCredentialsWithStatusUpdatedImport;
}

export type RefreshCredentialsDispatch = ThunkDispatch<
  AppState,
  null,
  ReturnType<typeof refreshCredentialsStart> | ReturnType<typeof refreshCredentialsEnd>
>;

type RefreshCredentialsThunk = (dispatch: RefreshCredentialsDispatch) => Promise<void>;

export interface CredentialsService {
  refreshCredentials: (props: RefreshCredentialsPayload) => RefreshCredentialsThunk;
}

const credentialsClient: CredentialsClient = {
  refreshCredentials: refreshCredentialsImport,
  signCredentialsWithStatusUpdated: signCredentialsWithStatusUpdatedImport,
};

export function createCredentialsService(client: CredentialsClient): CredentialsService {
  return {
    refreshCredentials:
      ({
        credentials,
        authenticate,
        refreshableItems,
        appUri,
        productNames,
      }: RefreshCredentialsPayload) =>
      async (dispatch) => {
        dispatch(refreshCredentialsStart());

        await client.refreshCredentials({
          id: credentials.id,
          authenticate,
          request: { appUri, productNames },
          refreshableItems,
        });

        const { id: credentialsId, statusUpdated } = credentials;

        const refreshedCredentials = await client.signCredentialsWithStatusUpdated(
          credentialsId,
          statusUpdated,
          dispatch
        );

        dispatch(refreshCredentialsEnd(refreshedCredentials as CredentialsEntity));
      },
  };
}

export const credentialsService = createCredentialsService(credentialsClient);
